"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
export default function Executive() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: ledeRef,
    isVisible: ledeVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: buttonsRef,
    isVisible: buttonsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: proofRef,
    isVisible: proofVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const proofPoints = [{
    value: "50+",
    label: "Industries served"
  }, {
    value: "100%",
    label: "Business focused"
  }, {
    value: "Scale",
    label: "Ready systems"
  }];
  return <section data-section-id="11181" id="executivebranding" className="text-foreground py-28 overflow-hidden bg-background/80 light !opacity-[1] md:py-40">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <div className="grid grid-cols-1 gap-12 items-center lg:grid-cols-12 lg:gap-16">
          
          <div className="lg:col-span-7 order-1">
            

            <h1 ref={headlineRef} className={`font-default font-normal text-foreground mt-6 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] delay-[80ms] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            fontSize: "clamp(2rem, 4vw + 0.5rem, 3.5rem)",
            fontWeight: 360,
            letterSpacing: "-0.022em",
            lineHeight: 1.1
          }}>
              Accounting Architecture for Today’s <span className="font-default italic inline-block border-b transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] opacity-100 translate-y-0 delay-[260ms]" style={{
              borderColor: 'var(--secondary)',
              borderBottomWidth: '1px',
              paddingBottom: '0.18em',
              fontWeight: '360'
            }}>Business Leaders</span>
            </h1>

            <p ref={ledeRef} className={`font-default text-foreground/80 mt-7 max-w-[52ch] transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] delay-[160ms] ${ledeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            fontSize: "clamp(1rem, 0.3vw + 0.9rem, 1.125rem)",
            lineHeight: 1.6,
            letterSpacing: "-0.005em"
          }}>More than just tax filing—we architect the financial systems that drive your scale. We optimize your revenue, assets, and cash flow in real-time to keep you profitable.</p>

            <div ref={buttonsRef} className={`mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] delay-[240ms] ${buttonsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <Button asChild className="group h-[52px] px-7 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg text-sm font-medium tracking-[0.01em] shadow-md transition-colors duration-[220ms]">
                
              </Button>

              <Button asChild variant="outline" className="h-[52px] px-7 border-[1.5px] border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground rounded-lg text-sm font-medium tracking-[0.01em] transition-colors duration-[220ms]">
                <Link to="#strategic-pillars">View our services</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 order-2">
            <div ref={imageRef} className={`transition-opacity duration-[700ms] ease-out ${imageVisible ? "opacity-100" : "opacity-0"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/executive_placeholder.webp" alt="ScaleBase Executive Blueprint" loading="lazy" className="w-full max-h-[400px] object-cover rounded-lg shadow-lg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/executive_placeholder.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/executive_placeholder.webp-wvc-sizes" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/9a9f516a-7b1d-4b05-bbb8-e5e9c2dd89e1.webp" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/2cbb91bb-4a46-406c-9e4b-d3f8912770f1.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/2cbb91bb-4a46-406c-9e4b-d3f8912770f1.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/2cbb91bb-4a46-406c-9e4b-d3f8912770f1.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707917/img/2cbb91bb-4a46-406c-9e4b-d3f8912770f1.png" src="https://scalebaseadvisors.com/wp-content/uploads/2026/06/2cbb91bb-4a46-406c-9e4b-d3f8912770f1.png" />
            </div>

            <div ref={proofRef} className={`mt-12 flex flex-wrap items-stretch gap-x-8 gap-y-4 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] delay-[320ms] ${proofVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              {proofPoints.map((point, i) => <div key={i} data-index={i} className="flex items-center gap-x-8">
                  {i > 0 && <span aria-hidden="true" className="hidden sm:block h-10 w-px bg-border" />}
                  <div className="flex flex-col">
                    <span className="font-default text-foreground" style={{
                  fontSize: "1.75rem",
                  fontWeight: 400,
                  letterSpacing: "-0.012em",
                  lineHeight: 1.1
                }}>
                      {point.value}
                    </span>
                    <span className="font-default text-xs text-muted-foreground tracking-[0.04em] mt-1">
                      {point.label}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}